import type { DestroyRef, WritableSignal } from '@angular/core';
import type { Subscription } from 'rxjs';
import type { Uuid } from '@common/types';
import type { WebWidgetEntity } from '@web/types';

import { Injectable, Injector, inject, runInInjectionContext, signal, untracked } from '@angular/core';
import { filter } from 'rxjs/operators';

import { Widgets } from '@common/enums';
import { widgetNameOf } from '@common/helpers/widget-envelope';

import type { BaseEntityFormOptions, IndentForm, LineForm } from '@sites/entity-signal-form';
import { createIndentForm, createLineForm } from '@sites/entity-signal-form';

import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { HistoryFacade } from '@web/store/history/history.facade';

/** Union of every widget form this service builds. Extend with the factory map below. */
export type WidgetSignalForm = LineForm | IndentForm;

type WidgetSignalFormFactory = (options: BaseEntityFormOptions<any>) => WidgetSignalForm;

/** Widget kind (the `<kind>` of `widget:<kind>`) → form factory. */
const FORM_FACTORIES: Partial<Record<string, WidgetSignalFormFactory>> = {
    [Widgets.LINE]: createLineForm,
    [Widgets.INDENT]: createIndentForm,
};

type WidgetFormEntry = {
    form: WidgetSignalForm;
    entitySignal: WritableSignal<WebWidgetEntity>;
    storeSub: Subscription;
    refCount: number;
};

/**
 * Ref-counted registry of widget signal forms, keyed by widget id — the
 * versioned-entity twin of `WebLegacyWidgetSignalFormService`. The control panel and
 * the view share one form; it is destroyed once the last consumer releases.
 *
 * Unlike the legacy service there is no default backfill: the entity is
 * validated (and lifted from legacy) at load, so its shape is already complete.
 * Writes go through `WidgetsActions` into `props` / `meta`, and history through
 * the batch pipeline, like grids and tabs.
 */
@Injectable({ providedIn: 'root' })
export class WebWidgetSignalFormService {
    private readonly injector = inject(Injector);
    private readonly widgetsFacade = inject(WidgetsFacade);
    private readonly historyFacade = inject(HistoryFacade);

    private readonly registry = new Map<Uuid, WidgetFormEntry>();

    public getOrCreate<F extends WidgetSignalForm = WidgetSignalForm>(widgetId: Uuid): F | null {
        const existing = this.registry.get(widgetId);
        if (existing) {
            existing.refCount++;
            return existing.form as F;
        }

        const entitySignal = signal<WebWidgetEntity>(undefined as unknown as WebWidgetEntity);

        // NgRx selectors emit synchronously, so the signal is populated before form construction.
        const storeSub = this.widgetsFacade
            .widgetById$(widgetId)
            .pipe(filter(Boolean))
            .subscribe((widget) => entitySignal.set(widget as unknown as WebWidgetEntity));

        const initialEntity = entitySignal();

        const createForm = initialEntity ? FORM_FACTORIES[widgetNameOf(initialEntity)] : undefined;
        if (!createForm) {
            storeSub.unsubscribe();
            console.warn(
                `[WebWidgetSignalFormService] no signal-form factory for widget ${widgetId}` +
                    (initialEntity ? ` (entityType: ${initialEntity.entityType})` : ' (entity not in store yet)') +
                    `; register it in FORM_FACTORIES.`
            );
            return null;
        }

        const form = runInInjectionContext(this.injector, () =>
            createForm({
                entity: entitySignal,
                initialData: initialEntity,
                onUpdate: (value: WebWidgetEntity, meta: { skipHistory: boolean }) => {
                    if (!meta.skipHistory) {
                        this.historyFacade.addBatchUpdateToHistory(
                            'widget',
                            { id: value.id, changes: untracked(entitySignal) },
                            { id: value.id, changes: value }
                        );
                    }
                    this.widgetsFacade.updateWidgetProperties(value.id, value);
                },
            })
        );

        // Seed the form synchronously so consumers don't observe an empty form value.
        form.syncFromEntity(structuredClone(initialEntity) as any);

        this.registry.set(widgetId, { form, entitySignal, storeSub, refCount: 1 });
        return form as F;
    }

    public getOrCreateScoped<F extends WidgetSignalForm = WidgetSignalForm>(
        widgetId: Uuid,
        destroyRef: DestroyRef
    ): F | null {
        const form = this.getOrCreate<F>(widgetId);
        // Only register the release when a form was actually created/retained —
        // on failure nothing was ref-counted.
        if (form) {
            destroyRef.onDestroy(() => this.release(widgetId));
        }
        return form;
    }

    public getForm<F extends WidgetSignalForm = WidgetSignalForm>(widgetId: Uuid): F | null {
        return (this.registry.get(widgetId)?.form as F) ?? null;
    }

    public release(widgetId: Uuid): void {
        const entry = this.registry.get(widgetId);
        if (!entry) {
            return;
        }

        entry.refCount--;
        if (entry.refCount <= 0) {
            this.deleteForm(widgetId);
        }
    }

    public deleteForm(widgetId: Uuid): void {
        const entry = this.registry.get(widgetId);
        if (!entry) {
            return;
        }

        entry.storeSub.unsubscribe();
        entry.form.destroy();
        this.registry.delete(widgetId);
    }
}
